import { AdditionalRate } from '../models/additional-rate.model';
import { MarginDetailsSaveRequest } from '../models/margin-details-save-request.model';
import { MarginExpense } from '../models/margin-expense.model';
import { MarginRtoDate } from '../models/margin-rto-date.model';
import { Margin } from '../models/margin.model';
import { LocumsMarginCalculatorSlice } from '../store/locums-margin-calculator.slice';

export function mapSliceToSaveRequest(slice: LocumsMarginCalculatorSlice): MarginDetailsSaveRequest {
    // ----- margin (core + calculations + notes) -----
    const margin: Margin = {
        marginId: slice.marginSetup.marginId ?? null,
        jobId: slice.jobDetails.jobId ?? 0,
        candidateId: slice.candidateDetails.candidateId ?? 0,
        marginStatusId: slice.marginSetup.marginStatusId, // always Drafting on save
        candidateExpertiseId: slice.candidateDetails.candidateExpertiseId ?? 0, // handles undefined
        professionId: slice.candidateDetails.professionId ?? null,
        expertiseId: slice.candidateDetails.expertiseId ?? null,
        expertiseYearsOfExperience: slice.candidateDetails.expertiseYearsOfExperience ?? null,

        // Dates & setup
        isAllInclusive: slice.marginSetup.isAllInclusive,
        contractStartDate: slice.marginSetup.contractStartDate,
        contractEndDate: slice.marginSetup.contractEndDate,
        days: slice.marginSetup.days,
        hoursPerShift: slice.marginSetup.hoursPerShift ?? null,
        payPeriodTypesId: slice.jobDetails.payPeriodTypesId,

        // Rates from setup
        billRate: slice.marginSetup.billRate ?? null,
        payRate: slice.marginSetup.payRate ?? null,
        overtimeBillRate: slice.marginSetup.overtimeBillRate ?? null,
        overtimePayRate: slice.marginSetup.overtimePayRate ?? null,
        holidayBillRate: slice.marginSetup.holidayBillRate ?? null,
        holidayPayRate: slice.marginSetup.holidayPayRate ?? null,
        overtimeRateFactor: slice.marginSetup.overtimeRateFactor ?? null,
        holidayRateFactor: slice.marginSetup.holidayRateFactor ?? null,

        // Calculations slice
        grossProfit: slice.calculations.grossProfit ?? null,
        grossProfitMargin: slice.calculations.grossProfitMargin ?? null,
        targetMargin: slice.calculations.targetMargin ?? null,
        shiftRevenue: slice.calculations.shiftRevenue ?? null,
        billableExpenses: slice.calculations.billableExpenses ?? null,
        contractFee: slice.calculations.contractFee ?? null,
        totalRevenue: slice.calculations.totalRevenue ?? null,
        shiftCost: slice.calculations.shiftCost ?? null,
        malpracticeInsurance: slice.calculations.malpracticeInsurance ?? null,
        malpracticeInsuranceRate:
            slice.calculations.malpracticeInsuranceRate === 0
                ? null
                : (slice.calculations.malpracticeInsuranceRate ?? null),
        accommodations: slice.calculations.accommodations ?? null,
        otherExpenses: slice.calculations.otherExpenses ?? null,
        totalCost: slice.calculations.totalCost ?? null,
        commissionTier: slice.calculations.commissionTier ?? null,
        estCommissionPerHour: slice.calculations.estimatedCommissionPerHour ?? null,
        estCommissionPerShift: slice.calculations.estimatedCommissionPerShift ?? null,
        totalCommission: slice.calculations.totalCommission ?? null,

        // Notes slice
        notes: slice.notes?.marginNotes ?? null
    };

    // ----- marginAdditionalRates -----
    const marginAdditionalRates: AdditionalRate[] = slice.additionalRates.additionalRates.map<AdditionalRate>((r) => ({
        marginAdditionalRateId: r.marginAdditionalRateId ?? null,
        marginId: r.marginId ?? null,
        additionalRateTypeId: r.additionalRateTypeId,
        marginCoverageTypeId: r.marginCoverageTypeId ?? null,
        billRate: r.billRate,
        payRate: r.payRate,
        expectedShifts: r.expectedShifts ?? null,
        expectedHours: r.expectedHours ?? null,
        isDeleted: r.isDeleted
    }));

    // ----- marginExpenses -----
    const marginExpenses: MarginExpense[] = slice.expenses.marginExpenses.map<MarginExpense>((e) => ({
        marginExpenseDetailId: e.marginExpenseDetailId ?? null,
        marginId: e.marginId ?? null,
        marginExpenseTypeId: e.marginExpenseTypeId,
        marginExpenseAttributeId: e.marginExpenseAttributeId,
        value: e.value,
        isDeleted: e.isDeleted,
        rowId: e.rowId
    }));

    // ----- rtoDates -----
    const rtoDates: MarginRtoDate[] = slice.marginRtoDates.rtoDates.map<MarginRtoDate>((e) => ({
        marginId: e.marginId ?? null,
        rtoDate: e.rtoDate ?? null
    }));

    return {
        margin,
        marginExpenses,
        marginAdditionalRates,
        marginRtoDates: rtoDates
    };
}
